/**
 * DonationModal.tsx
 *
 * Buy Me a Coffee donation modal for web
 * Redirects to buymeacoffee.com/vialogos
 *
 * Simple, no payment processing required
 */
import { useState } from 'react';
import { useTheme } from '../../services/ThemeContext';

const BUymeACoffee_URL = 'https://buymeacoffee.com/vialogos';

interface DonationModalProps {
  onClose: () => void;
}

type DonationState = 'idle' | 'redirecting';

const DonationModal: React.FC<DonationModalProps> = ({ onClose }) => {
  const { currentTheme: colors } = useTheme();
  const [state, setState] = useState<DonationState>('idle');

  const handleDonate = () => {
    setState('redirecting');
    // Open Buy Me a Coffee in new tab
    window.open(BUymeACoffee_URL, '_blank');
    setState('idle');
    onClose(); // Close modal after opening
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          backgroundColor: colors.surface,
          borderRadius: '1rem',
          maxHeight: '90vh',
          overflow: 'auto',
          width: '100%',
          maxWidth: '400px',
          padding: '0'
        }}
      >
        {/* Header */}
        <div style={{ borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ padding: '1rem' }}>
            <h2 style={{ color: colors.primary, textAlign: 'center', fontSize: '18px', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              Support the Developer
            </h2>
            <p style={{ color: colors.textMuted, textAlign: 'center', fontSize: '12px' }}>
              Your support helps keep this app free
            </p>
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            zIndex: 10,
          }}
        >
          <span
            style={{
              position: 'relative',
              width: '16px',
              height: '16px',
              display: 'inline-block',
            }}
          >
            <span
              style={{
                position: 'absolute',
                width: '2px',
                height: '16px',
                backgroundColor: colors.textMuted,
                left: '7px',
                top: 0,
                transform: 'rotate(45deg)',
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '2px',
                height: '16px',
                backgroundColor: colors.textMuted,
                left: '7px',
                top: 0,
                transform: 'rotate(-45deg)',
              }}
            />
          </span>
        </button>

        {/* Content */}
        <div style={{ padding: '1rem' }}>
          <p style={{ color: colors.textMain, fontSize: '14px', textAlign: 'center', lineHeight: '1.5', marginBottom: '0.75rem' }}>
            If you've enjoyed using Via Logos and would like to support its development, consider buying me a coffee! ☕
          </p>

          <p style={{ color: colors.textMuted, fontSize: '12px', textAlign: 'center', lineHeight: '1.4', marginBottom: '1rem' }}>
            Donations are handled through Buy Me a Coffee, a separate platform. The amounts are fixed on their site ($3, $5, $10, etc.).
          </p>

          {/* Buy Me a Coffee Button */}
          <button
            onClick={handleDonate}
            disabled={state === 'redirecting'}
            style={{
              width: '100%',
              backgroundColor: state === 'redirecting' ? colors.border : '#FFDD00',
              color: state === 'redirecting' ? colors.textMuted : '#000000',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: state === 'idle' ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (state === 'idle') {
                e.currentTarget.style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {state === 'redirecting' ? (
              <>
                <div
                  className="animate-spin"
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #000000',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                  }}
                />
                <span>Opening...</span>
              </>
            ) : (
              <>
                <span>☕</span>
                <span>Buy Me a Coffee</span>
              </>
            )}
          </button>

          <p style={{ color: colors.textMuted, fontSize: '11px', textAlign: 'center', marginTop: '0.75rem' }}>
            Opens buymeacoffee.com/vialogos in a new tab
          </p>
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${colors.border}` }}>
          <div style={{ padding: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                width: '100%',
                backgroundColor: colors.border,
                color: colors.textMain,
                border: `2px solid ${colors.textMuted}`,
                cursor: 'pointer',
                padding: '10px 16px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationModal;
